import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { defaultSYS3020_R01Dto, SYS3020_R01Dto } from "src/containers/mes/SYS/SYS3020_R01/Dto_SYS3020_R01";
import { useSYS3020_R01Store } from "src/containers/mes/SYS/SYS3020_R01/Store_SYS3020_R01";
import useUiuxStore from "src/store/useUiuxStore";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, getSwitchButtonColumnOptions, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags } from "src/utils/grid/gridStyleUtils";

const COLUMN_STATE_KEY = 'SYS3020_R01-menu-authority-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SYS3020_R01Dto> | null>;
	defaultSYS3020_R01DetailRow: SYS3020_R01Dto;
}

export const DetailTable_SYS3020_R01 = (props: Props) => {
	const { gridRef, defaultSYS3020_R01DetailRow } = props;

	const {
		SYS3020_R01DetailList, setSYS3020_R01DetailList,
		insertSYS3020_R01DetailList, removeSYS3020_R01Detail, updateSYS3020_R01Detail,
		deletedSYS3020_R01DetailList, addDeletedSYS3020_R01DetailList,
		selectedSYS3020_R01List, addSelectedSYS3020_R01List, removeSelectedSYS3020_R01List,
		initSelectedSYS3020_R01List, allSelectedSYS3020_R01List,
		originalSYS3020_R01DetailList,
		detailColumnState, setDetailColumnState,
		detailScrollTop, setDetailScrollTop,
	} = useSYS3020_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();

	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const [clickable, setClickable] = useState(true);

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: SYS3020_R01DetailList,
		defaultGridData: defaultSYS3020_R01DetailRow,
		selectedRow: selectedSYS3020_R01List,
		originalGridData: originalSYS3020_R01DetailList,
		updateRow: (index: number, data: any) => updateSYS3020_R01Detail(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		menuCd: '메뉴코드',
		menuNm: '메뉴명',
		useYn: '사용여부',
		queryAut: '조회',
		insertAut: '등록',
		deleteAut: '삭제',
		saveAut: '저장',
		printAut: '출력',
		excelAut: '엑셀',
		remark: '비고',
	};

	const insertRowHandler = useCallback((rowIndex: number, clickEvent: React.MouseEvent) => {
		InsertRowHandler({
			event: clickEvent,
			dataList: SYS3020_R01DetailList,
			rowData: SYS3020_R01DetailList[rowIndex],
			insertRow: insertSYS3020_R01DetailList,
			defaultRowData: defaultSYS3020_R01DetailRow,
			setClickable,
		});
	}, [SYS3020_R01DetailList, insertSYS3020_R01DetailList, defaultSYS3020_R01DetailRow, setClickable]);

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['menuNm', 'useYn'],
		insertedDisabledCells: ['useYn'],
		commonDisabledCells: ['menuCd', 'menuNm', 'useYn'],
		
		pointerCells: ['queryAut', 'insertAut', 'deleteAut', 'saveAut', 'printAut', 'excelAut', 'remark'],
		insertedPointerCells: ['menuCd', 'menuNm'],
		commonPointerCells: [''],
		
		alignLeftCells: ['']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<SYS3020_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedSYS3020_R01List,
			deletedList: deletedSYS3020_R01DetailList,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }};
	}, [selectedSYS3020_R01List, deletedSYS3020_R01DetailList, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns, createCellClassRules]);

	const baseColumnDefs: ColDef<SYS3020_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: SYS3020_R01Dto) => renderIndexCell(
				params,
				(innerParams) => getRowStyleFlags(innerParams, {
					deletedList: deletedSYS3020_R01DetailList,
					selectedList: selectedSYS3020_R01List,
					dataList: SYS3020_R01DetailList,
				}),
				(rowIndex, clickEvent) => {
					if (clickEvent.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, clickEvent); }
					} else {
						insertRowHandler(rowIndex, clickEvent);
					}
				},
				(rowData) => RemoveRowHandler(
					gridRef,
					SYS3020_R01DetailList,
					rowData,
					setClickable,
					removeSYS3020_R01Detail,
					(valueIndex) => addDeletedSYS3020_R01DetailList([valueIndex.toString()])
				),
			)
		),
		{
			headerName: "메뉴코드", colId: "menuCd", field: "menuCd", width: 100,
			editable: (params) => params.data?.inserted === true,
			pasteable: true,
		},
		{
			headerName: "메뉴명", colId: "menuNm", field: "menuNm", width: 180,
			editable: false,
		},
		// { 
		// 	headerName: "사용여부", field: "useYn", colId: "useYn", width: 80, 
		// 	...getSwitchButtonColumnOptions(
		// 		'useYn',
		// 		handleSwitchButtonHandler,
		// 		headerNames,
		// 		aggridSwitchButtonWrapperStyle,
		// 		(params) => params.data?.inserted !== true,
		// 	),
		// },	
		{
			headerName: "조회", colId: "queryAut", field: "queryAut", width: 70,
			...getSwitchButtonColumnOptions('queryAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "등록", colId: "insertAut", field: "insertAut", width: 70,
			...getSwitchButtonColumnOptions('insertAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "삭제", colId: "deleteAut", field: "deleteAut", width: 70,
			...getSwitchButtonColumnOptions('deleteAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "저장", colId: "saveAut", field: "saveAut", width: 70,
			...getSwitchButtonColumnOptions('saveAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "출력", colId: "printAut", field: "printAut", width: 70,
			...getSwitchButtonColumnOptions('printAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "엑셀", colId: "excelAut", field: "excelAut", width: 70,
			...getSwitchButtonColumnOptions('excelAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
		},
		{
			headerName: "비고", colId: "remark", field: "remark", width: 120,
			editable: true,
		},
	], [SYS3020_R01DetailList, selectedSYS3020_R01List, deletedSYS3020_R01DetailList, insertRowHandler, handleSwitchButtonHandler, createCellClassRules]);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<SYS3020_R01Dto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<SYS3020_R01Dto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	const handleRowClick = useRowSelectionHandler({
		dataList: SYS3020_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedSYS3020_R01List,
		deletedList: deletedSYS3020_R01DetailList,
		addSelected: addSelectedSYS3020_R01List,
		removeSelected: removeSelectedSYS3020_R01List,
		clickable, setClickable
	});


	const handleCellChange = useCellChangeHandler({
		dataList: SYS3020_R01DetailList,
		originalDataList: originalSYS3020_R01DetailList,
		updateRow: updateSYS3020_R01Detail,
	});


	// 컬럼 클릭: 1회 클릭 → 편집 포커스, 더블클릭 → 프로그램 팝업 (WMS DetailTable 패턴)
	const commonInputColumns = ['queryAut', 'insertAut', 'deleteAut', 'saveAut', 'printAut', 'excelAut', 'remark'];
	const editableColumns = ['menuCd', ...commonInputColumns];

	const openMenuSearchModal = useCallback((foundData: SYS3020_R01Dto) => {
		setCodeSearchModalProps({
			category: '프로그램',
			search: foundData.menuCd || '',
			formName: 'SYS3020_R01',
			gridRef,
			onConfirm: (selectedMenu: { menuCd: string; menuNm: string }) => {
				updateSYS3020_R01Detail(foundData.valueIndex, {
					...foundData,
					menuCd: selectedMenu.menuCd,
					menuNm: selectedMenu.menuNm,
					isModified: true,
				});
			},
		});
	}, [setCodeSearchModalProps, updateSYS3020_R01Detail, gridRef]);

	const cellClickActions: CustomCellClickAction<SYS3020_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				if (field === 'menuCd') return !!data?.inserted;
				return commonInputColumns.includes(field);
			},
			action: (e) => {
				e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
			},
		},
	], [commonInputColumns]);

	const handleCellClick = CreateCellClickHandler(SYS3020_R01DetailList, 'valueIndex', cellClickActions);

	const cellEventHandlers = useDetailTableEvents({
		detailList: SYS3020_R01DetailList as SYS3020_R01Dto[],
		editableColumns,
	});

	const cellDoubleClickActions: CustomCellDoubleClickAction<SYS3020_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => (field === 'menuCd' || field === 'menuNm') && !!data?.inserted,
			action: (_e, foundData) => openMenuSearchModal(foundData),
		},
	], [openMenuSearchModal]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(SYS3020_R01DetailList, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<SYS3020_R01Dto>) => {
		postSortHandler(params, SYS3020_R01DetailList, originalSYS3020_R01DetailList, setSYS3020_R01DetailList);
	}, [SYS3020_R01DetailList, originalSYS3020_R01DetailList]);
  
	// 하단 합계(메뉴 권한 수)
	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		menuCd: SYS3020_R01DetailList.length.toLocaleString() + "건",
		editable: false,
	}]), [SYS3020_R01DetailList.length]);

	return (
		<GridContextWrapper<SYS3020_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={SYS3020_R01DetailList}
			setPaddedGridData={setSYS3020_R01DetailList}
			initSelected={initSelectedSYS3020_R01List}
			defaultRowData={defaultSYS3020_R01DetailRow}
			allSelected={allSelectedSYS3020_R01List}
			baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setDetailColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<SYS3020_R01Dto>
				ref={gridRef}
				rowData={SYS3020_R01DetailList}
				getRowId={getRowId}
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<SYS3020_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedSYS3020_R01List,
					deletedList: deletedSYS3020_R01DetailList
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
				// Grid_Select에 공통 로직 처리를 위한 props 전달
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setDetailColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={detailScrollTop}
				setScrollTop={setDetailScrollTop}
				
			/>
		</GridContextWrapper>
	);
};

export default DetailTable_SYS3020_R01; 
